
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { VueGoodTable } from 'vue-good-table';
import Toasted from 'vue-toasted';
import 'vue-good-table/dist/vue-good-table.css';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import CenterOnParent from '../../util/CenterOnParent';
import TranslationEditModal from './Configure/TranslationEditModal';
import LocalizationStore from '../../../stores/LocalizationStore';
import { LocaleStringResource } from '../../../../backend/backendapi';
import { groupBy, WindowType } from '../../../util';

Vue.use(Toasted, { duration: 2000 });

declare var window: WindowType;

export type Translation = {
  resourceName: string;
  translations: Map<string, LocaleStringResource>;
  inheritedTranslations: Map<string, LocaleStringResource>;
}

@Component({
  components: {
    CenterOnParent,
    TranslationEditModal,
    VueGoodTable,
  },
})
export default class Configure extends Vue {
  private readonly store = new LocalizationStore();

  editInherited = false;

  get canEditInherited(): boolean {
    return window.application.check2.isSysAdmin;
  }

  get columnsConfig(): unknown[] {
    return [
      {
        label: this.$t('revobase.localization.model.localeStringResource.resourceName').toString(),
        field: (e: Translation) => e.resourceName,
      },
      ...this.store.locales
          .map(locale => ({
            label: locale,
            field: (e: Translation) => [e.inheritedTranslations.get(locale), e.translations.get(locale)],
            isLocaleColumn: true,
          })),
      {
        label: this.$t('general.actions').toString(),
        field: 'actions',
        width: '5em',
      },
    ];
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get rows(): Translation[] {
    const resourceNames = new Set([
      ...this.store.languages.flatMap(e => e.translations.map(e => e.resourceName!)),
      ...this.store.inheritedLanguages.flatMap(e => e.translations.map(e => e.resourceName!)),
    ]);
    const translations = this.store.languages.flatMap(e => e.translations);
    const inheritedTranslations = this.store.inheritedLanguages.flatMap(e => e.translations);
    const translationsByResourceName = groupBy(translations, e => e.resourceName);
    const inheritedTranslationsByResourceName = groupBy(inheritedTranslations, e => e.resourceName);
    return [...resourceNames].map(resourceName => ({
      resourceName: resourceName,
      translations: new Map((translationsByResourceName.get(resourceName) ?? [])
          .map(e => [this.store.languagesById.get(e.languageId!)!.language.languageCulture!, e])),
      inheritedTranslations: new Map((inheritedTranslationsByResourceName.get(resourceName) ?? [])
          .map(e => [this.store.inheritedLanguagesById.get(e.languageId!)!.language.languageCulture!, e])),
    }));
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  async mounted(): Promise<void> {
    await this.store.reload();
  }

  editDefinition(translation: Translation): void {
    (this.$refs.editModal as TranslationEditModal).open(this.rows.find(e => e.resourceName === translation.resourceName)!, false);
  }

  formatTranslationsCell(x: [LocaleStringResource | undefined, LocaleStringResource | undefined], editInherited: boolean): string {
    if (editInherited) {
      return x[0]?.resourceValue ?? '';
    } else {
      return x[1]?.resourceValue ?? x[0]?.resourceValue ?? '';
    }
  }

  newDefinition(): void {
    const row = {
      resourceName: '',
      inheritedTranslations: new Map(),
      translations: new Map(),
    } as Translation;
    (this.$refs.editModal as TranslationEditModal).open(row, true);
  }
}
